import { useCallback, useMemo, useState } from 'react';

import { useAlert } from '@app/hooks';

import { FormProps, FormValidation } from '../types';
import { getFieldValue } from '../utils';

export function useForm<T>(props: FormProps<T>) {
  const {
    instance,
    onSubmit,
    onAlternativeSubmit,
    onChange,
   } = props;

  const [titles, setTitles] = useState<Record<string, string>>({});
  const [required, setRequired] = useState<string[]>([]);
  const [validations, setValidations] = useState<Partial<Record<keyof T, FormValidation>>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setisLoading] = useState<boolean>(false);
  const { warning } = useAlert();

  const addValidation = useCallback((field: keyof T, title: string, validation: FormValidation) => {
    setValidations((v) => ({ ...v, [field]: validation }));
    setTitles((t) => ({ ...t, [field]: title }));
  }, []);

  const addRequired = useCallback((field: keyof T, title: string) => {
    setRequired((r) => [...r, field.toString()]);
    setTitles((t) => ({ ...t, [field]: title }));
  }, []);

  const validate = useCallback(async () => {
    setErrors({});
    let isValid = !required.some((field) => {
      const value = getFieldValue(instance, field);
      if (value === undefined || value === null) {
        setErrors((e) => ({...e, [field]: 'Обязательное поле'}));
        warning(`Не заполнено обязательное поле: ${titles[field]}`);
        return true;
      }
    });
    if (!isValid) {
      return Promise.resolve(false);
    }

    for(const field in validations) {
      const value = getFieldValue(instance, field);
      const result = validations[field]?.validation(value);
      const isValidField = result instanceof Promise ? await result : result;
      if (isValidField !== true) {
        setErrors((e) => ({...e, [field]: validations[field].message || isValidField || 'Ошибка в значении'}));
        warning(validations[field].message || isValidField || `Ошибка в значении: ${titles[field]}`);

        isValid = false;
      }
    }

    return Promise.resolve(isValid);
  }, [required, validations, instance]);

  const onSubmitForm = useCallback(async () => {
    setisLoading(true);
    const isValid = await validate();
    isValid && await onSubmit(instance);
    setisLoading(false);
  }, [onSubmit, validate, instance]);

  const onAlternativeSubmitForm = useMemo(() => {
    if(!onAlternativeSubmit) {return undefined;}
    return async () => {
      setisLoading(true);
      const isValid = await validate();
      isValid && onAlternativeSubmit(instance);
      setisLoading(false);
    };
  }, [onAlternativeSubmit, validate, instance]);

  return {onSubmitForm, onAlternativeSubmitForm, contextValue: useMemo(() => ({
    instance,
    errors,
    addRequired,
    addValidation,
    isLoading,
    onChange,
  }), [addRequired, addValidation, errors, instance, onChange, isLoading])};
}